import { ESessionType } from '@mindverse/accessor-open';
import dynamic from 'next/dynamic';
import { memo } from 'react';

import useMindStorage, { EStorageKey } from '@/components/chat-app/hooks/useMindStorage';
import { useSessionAvatarMap } from '@/components/chat-app/hooks/useSessionAvatarMap';
import type { MessageModel } from '@/components/chat-app/hooks/useSessionMsg';
import { useModel } from '@/hooks/useModel';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';

import type MessageItem from '../../model/message-item';
import { MessageItemType } from '../../model/message-item';

// Lazy load message components - only loaded based on message type
const ReceivedBot = dynamic(() => import('./ReceivedBot'), {
  ssr: false
});
const ReceivedUser = dynamic(() => import('./ReceivedUser'), {
  ssr: false
});
const SendBot = dynamic(() => import('./SendBot'), {
  ssr: false
});
const SendUser = dynamic(() => import('./SendUser'), {
  ssr: false
});
const System = dynamic(() => import('./system'), {
  ssr: false
});

export interface IProps {
  messageModel: MessageModel;
  isLoading: boolean;
  showActions: boolean;
  isLast: boolean;
}

function Message(props: IProps) {
  const { messageModel, isLoading, showActions, isLast } = props;
  const { userProfile } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile
  }));
  const userId = userProfile?.userId;
  const userAvatar = userProfile?.avatar;

  const { homepageData } = useHomepageContext();

  const [sessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);
  const avatarMap = useSessionAvatarMap();

  if (
    messageModel.sendUserId &&
    sessionInfo?.type &&
    [ESessionType.GROUP, ESessionType.OTHERBOT, ESessionType.FRIEND].includes(sessionInfo.type)
  ) {
    if (messageModel.type === MessageItemType.system) {
      return messageModel.list.map((msgItem, index) => {
        return <System key={msgItem.messageId + index} message={msgItem as MessageItem} />;
      });
    }

    if (
      messageModel.sendUserId === userId ||
      messageModel.sendUserId === homepageData?.visitUserId
    ) {
      if (messageModel.type === MessageItemType.umm) {
        return <SendBot key={messageModel.messageId} avatar={userAvatar} message={messageModel} />;
      }

      return messageModel.list.map((msgItem) => {
        return (
          <SendUser
            key={msgItem.seqId + String((msgItem as any)?.data?.templates)}
            avatar={userAvatar}
            message={msgItem as MessageItem}
          />
        );
      });
    }

    if (messageModel.type === MessageItemType.umm) {
      const _avatar = avatarMap.get(messageModel.sendUserId);

      return (
        <ReceivedBot
          avatar={_avatar}
          isLast={isLast}
          isLoading={isLoading}
          message={messageModel}
          showActions={false}
        />
      );
    }

    return messageModel.list.map((msgItem, index) => {
      const _avatar = avatarMap.get(msgItem.sendUserId);
      const defaultAvatar =
        'https://mindverseglobal.mindverse.com/front-img/crm/1766814853894/defaultImage.png';

      return (
        <ReceivedUser
          key={msgItem.messageId + index}
          avatar={_avatar || defaultAvatar}
          isLast={isLast && index === messageModel.list.length - 1}
          message={msgItem as MessageItem}
        />
      );
    });
  }

  // 兼容老逻辑,即type=Mebot
  switch (messageModel.type) {
    case MessageItemType.umm:
      return (
        <ReceivedBot
          isLast={isLast}
          isLoading={isLoading}
          message={messageModel}
          showActions={showActions}
        />
      );
    case MessageItemType.client:
      return messageModel.list.map((msgItem) => {
        return (
          <SendUser
            key={msgItem.seqId + String((msgItem as any)?.data?.templates)}
            message={msgItem as MessageItem}
          />
        );
      });
    case MessageItemType.system:
      return messageModel.list.map((msgItem, index) => {
        return <System key={msgItem.messageId + index} message={msgItem as MessageItem} />;
      });
  }
}

export default memo(Message);
